import { defaultFilters, defaultRequirementState } from '../state/requirements';

import type { Filters, Requirement } from '../state/requirements';

/**
 * Helper to create a complete Requirement object with all required properties.
 * This ensures TypeScript requirements are met while allowing you to override specific properties.
 *
 * @example
 * // Create requirement with just filters
 * const requirement = createRequirement({
 *   filters: createRequirementFilters({ product: createProductFilter(products) })
 * });
 *
 * // Use in requirements
 * const state = buildDiscountInitialState({
 *   requirements: {
 *     1: createRequirement({
 *       filters: createRequirementFilters({ product: createProductFilter(products) })
 *     })
 *   }
 * });
 */
export function createRequirement(overrides: Partial<Requirement> = {}): Requirement {
  return {
    ...defaultRequirementState,
    ...overrides,
    // If filters are provided, merge them with defaults to ensure all filter keys exist
    filters: overrides.filters
      ? createRequirementFilters(overrides.filters as Partial<Filters>)
      : defaultRequirementState.filters,
  };
}

/**
 * Helper to create a complete Filters object with all required filter properties.
 * This ensures TypeScript requirements are met while allowing you to override specific filters.
 *
 * @example
 * // Create filters with just product filter
 * const filters = createRequirementFilters({
 *   product: createProductFilter(products)
 * });
 *
 * // Use in requirements
 * const state = buildDiscountInitialState({
 *   requirements: {
 *     1: createRequirement({
 *       filters: createRequirementFilters({ product: createProductFilter(products) })
 *     })
 *   }
 * });
 */
export function createRequirementFilters(overrides: Partial<Filters> = {}): Filters {
  return {
    ...defaultFilters,
    ...overrides,
  };
}
